import React, { useState, useEffect, useRef } from 'react';
import { authAPI } from '../services/api';
import { useAppState, useAppDispatch, actions } from '../context/AppContext';
import { useUserSuggest } from '../hooks/useUserSuggest';
import { useRecentSearches } from '../hooks/useRecentSearches';
import type { UserSuggestion } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRightFromBracket,
  faSearch,
  faXmark,
  faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import '../styles/Header.css';
import { UserRoles } from 'types';

function Header(): React.ReactElement | null {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [inputValue, setInputValue] = useState(''); // Local state for input
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Get state from context
  const appState = useAppState();
  const dispatch = useAppDispatch();
  const { currentUser: user, searchEmail, searchMode, searchedUserName } = appState;

  // Autocomplete suggestions
  const {
    suggestions,
    loading: _suggestionsLoading,
    clearSuggestions,
  } = useUserSuggest(inputValue);

  // Recent searches
  const { recentSearches, addRecentSearch } = useRecentSearches();

  // Get environment from build-time environment variable
  const environment = import.meta.env.MODE;

  // Close search bar and clear input when exiting search mode
  useEffect(() => {
    if (!searchMode) {
      setInputValue('');
      setSearchOpen(false);
    }
  }, [searchMode]);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
    // Logout anyway on client side
    dispatch(actions.setCurrentUser(null));
    dispatch(actions.setSearchEmail('', false));
    dispatch(actions.setShowLogoutPage(true));
    dispatch(actions.clearAllCache());
  };

  const handleSuggestionSelect = (user: UserSuggestion) => {
    setInputValue(user.email);
    setShowSuggestions(false);
    clearSuggestions();
    addRecentSearch(user);
    dispatch(actions.setSearchEmail(user.email, true));
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleDropdownLogout = () => {
    setDropdownOpen(false);
    handleLogout();
  };

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
  };

  const handleSearchBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // Check if the new focus target is still within the search container (including suggestions)
    const relatedTarget = e.relatedTarget as Node | null;
    if (
      searchContainerRef.current &&
      relatedTarget &&
      searchContainerRef.current.contains(relatedTarget)
    ) {
      return; // Focus is still within the search area
    }
    setShowSuggestions(false);
    setInputValue('');
    clearSuggestions();
    // Only close search bar if not in search mode (not impersonating)
    if (!searchMode) {
      setTimeout(() => {
        setSearchOpen(false);
      }, 200);
    }
  };

  const handleReset = () => {
    setInputValue(''); // Clear local input
    setShowSuggestions(false);
    clearSuggestions();
    dispatch(actions.setSearchEmail('', false));
    setSearchOpen(false); // Close search bar when reset is clicked
  };

  // Get user initials
  const getUserInitials = (): string => {
    if (!user) return 'U';
    if (user.name) {
      return user.name[0].toUpperCase();
    }
    return user.email ? user.email[0].toUpperCase() : 'U';
  };

  // Guard: Don't render if no user (after hooks to comply with rules of hooks)
  if (!user) {
    return null;
  }

  return (
    <header className="header" data-testid="header-container">
      <div className="header-nav">
        <div className="header-left">
          <div className="header-logo" data-testid="header-logo">
            <img src="/images/sn-logo.svg" alt="ServiceNow Logo" className="logo-img" />
            <span className="logo-divider">|</span>
            <span className="logo-text" data-testid="header-logo-text">
              NetView
            </span>
            {environment && environment !== 'production' && (
              <span className={`env-badge env-${environment}`}>{environment.toUpperCase()}</span>
            )}
          </div>
          {/* Navigation links placeholder - can be added later */}
          <nav className="header-nav-links">{/* Future navigation items go here */}</nav>
        </div>
        <div className="header-right">
          {/* Collapsible Search Bar - Only show for admins,viewer */}

          {(user.userRole === UserRoles.ADMIN || user.userRole === UserRoles.VIEWER) && (
            <>
              {searchMode ? (
                <div className="header-viewing-chip" data-testid="header-viewing-chip">
                  <span className="header-viewing-label">Viewing:</span>
                  <span className="header-viewing-name">{searchedUserName || searchEmail}</span>
                  <button
                    type="button"
                    className="header-viewing-close"
                    onClick={handleReset}
                    title="Stop viewing user"
                    data-testid="header-viewing-close-btn"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
              ) : (
                <div
                  className={`header-search-container ${searchOpen ? 'open' : ''}`}
                  ref={searchContainerRef}
                >
                  {!searchOpen ? (
                    <button
                      type="button"
                      className="search-toggle-btn"
                      onClick={toggleSearch}
                      title="Search User"
                      data-testid="header-search-toggle-btn"
                    >
                      <FontAwesomeIcon icon={faSearch} />
                    </button>
                  ) : (
                    <>
                      <div className="header-search" onBlur={handleSearchBlur}>
                        <input
                          type="text"
                          value={inputValue}
                          onChange={(e) => {
                            setInputValue(e.target.value.toLowerCase());
                            setShowSuggestions(true);
                          }}
                          onFocus={() => setShowSuggestions(true)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.preventDefault();
                          }}
                          placeholder="Search User"
                          className="search-input"
                          autoFocus
                          data-testid="header-search-input"
                        />
                      </div>
                      {showSuggestions && suggestions.length > 0 && (
                        <ul className="search-suggestions" data-testid="header-search-suggestions">
                          {suggestions.map((s) => (
                            <li key={s.email}>
                              <button
                                type="button"
                                className="search-suggestion-item"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleSuggestionSelect(s);
                                }}
                                data-testid="header-search-suggestion"
                              >
                                <span className="suggestion-name">{s.name || s.email}</span>
                                <span className="suggestion-email">{s.email}</span>
                                {s.department && (
                                  <span className="suggestion-dept">{s.department}</span>
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {showSuggestions &&
                        suggestions.length === 0 &&
                        inputValue.length < 3 &&
                        recentSearches.length > 0 && (
                          <ul className="search-suggestions" data-testid="header-recent-searches">
                            <li className="recent-searches-label">Recent</li>
                            {recentSearches.map((s) => (
                              <li key={s.email}>
                                <button
                                  type="button"
                                  className="search-suggestion-item"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSuggestionSelect(s);
                                  }}
                                  data-testid="header-recent-search"
                                >
                                  <span className="suggestion-name">{s.name || s.email}</span>
                                  <span className="suggestion-email">{s.email}</span>
                                  {s.department && (
                                    <span className="suggestion-dept">{s.department}</span>
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
          {/* User Profile with Dropdown */}
          <div className="user-profile">
            <button
              className="profile-button"
              onClick={toggleDropdown}
              data-testid="header-profile-btn"
            >
              <div className="profile-avatar" data-testid="header-profile-avatar">
                {getUserInitials()}
              </div>
            </button>

            {dropdownOpen && (
              <>
                <div className="dropdown-overlay" onClick={() => setDropdownOpen(false)} />
                <div className="profile-dropdown" data-testid="header-profile-dropdown">
                  <div className="dropdown-header">
                    <div className="dropdown-avatar">{getUserInitials()}</div>
                    <div className="dropdown-user-info">
                      <div className="dropdown-user-name" data-testid="header-dropdown-user-name">
                        {user.name || user.email}
                      </div>
                      <div className="dropdown-user-email" data-testid="header-dropdown-user-email">
                        {user.email}
                      </div>
                      {(user.userRole === UserRoles.ADMIN ||
                        user.userRole === UserRoles.VIEWER) && (
                        <div
                          className="dropdown-admin-tag"
                          data-testid="header-dropdown-role-badge"
                        >
                          {user.userRole}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="dropdown-divider" />
                  {user.userRole === UserRoles.ADMIN && (
                    <>
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          setDropdownOpen(false);
                          dispatch(actions.setCurrentView('admin'));
                        }}
                        data-testid="header-admin-portal-btn"
                      >
                        <FontAwesomeIcon icon={faUserShield} />
                        Admin Portal
                      </button>
                      <div className="dropdown-divider" />
                    </>
                  )}
                  <button
                    className="dropdown-item"
                    onClick={handleDropdownLogout}
                    data-testid="header-logout-btn"
                  >
                    <FontAwesomeIcon icon={faRightFromBracket} />
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
